// DynamicDashboard — yeni-design outer chrome (Phase 2 rebuild).
// Widget components (StatWidget, *ChartWidget, etc.) keep their existing internals
// (Antd + recharts). Only the surrounding grid + page header + empty state move
// to yeni-design Tailwind primitives.
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import { useState } from 'react';
import type { ReactElement } from 'react';
import {
  StatWidget,
  KpiWidget,
  StatSparklineWidget,
  LineChartWidget,
  BarChartWidget,
  PieChartWidget,
  DonutChartWidget,
  HBarWidget,
  TableSummaryWidget,
  FieldBreakdownWidget,
  BranchControlWidget,
  OpenTicketsWidget,
  KabinPlanWidget,
  AreaChartWidget,
} from './widgets';
import { EmptyState, PageHeader, SectionCard } from './yeni-design/primitives';
import { I } from './yeni-design/icons';
import { PeriodTabs, type DashboardPeriod } from './PeriodTabs';
import type {
  DashboardWidget,
  GridSpan,
  ReportConfig,
} from '../types/panelConfig';

dayjs.locale('tr');

interface DynamicDashboardProps {
  widgets: DashboardWidget[];
  reports: ReportConfig[];
  /** Rapor kodu → ds id; menüden gizli kaynaklar için (S308). */
  widgetSources?: Record<string, number>;
}

type KaynakBul = (code: string | undefined) => number | undefined;

// 12-col grid mapping. 1=quarter, 2=half, 3=three-quarters, 4=full.
const colSpanMap: Record<GridSpan, string> = {
  1: 'lg:col-span-3',
  2: 'lg:col-span-6',
  3: 'lg:col-span-9',
  4: 'lg:col-span-12',
};

// Backward compat: older panel configs may still return 'stat-card' before the
// API deploys the widget_type rename migration. Normalize at read time.
function normalizeWidget(widget: DashboardWidget): DashboardWidget {
  const raw = widget as unknown as { type: string };
  if (raw.type === 'stat-card') {
    return { ...(widget as object), type: 'stat' } as DashboardWidget;
  }
  return widget;
}

function renderWidget(
  widget: DashboardWidget,
  kaynak: KaynakBul,
  parameters: Record<string, string>,
  isSingleDay: boolean,
): ReactElement | null {
  // branch-control resolves two source IDs (summaryReportCode + kasaReportCode)
  // from config — it does NOT use widget.reportCode. Handle it before the generic
  // sourceId guard so it never depends on widget.reportCode resolving.
  if (widget.type === 'branch-control') {
    const summary = kaynak(widget.config.summaryReportCode);
    const kasa = kaynak(widget.config.kasaReportCode);
    if (!summary || !kasa) return null;
    return <BranchControlWidget config={widget.config} summarySourceId={summary} kasaSourceId={kasa} parameters={parameters} />;
  }

  const sourceId = kaynak(widget.reportCode);
  if (!sourceId) return null;

  switch (widget.type) {
    case 'stat':
      return <StatWidget widget={widget} sourceId={sourceId} parameters={parameters} />;
    case 'kpi':
      return <KpiWidget widget={widget} sourceId={sourceId} parameters={parameters} />;
    case 'stat-sparkline':
      return <StatSparklineWidget widget={widget} sourceId={sourceId} parameters={parameters} />;
    case 'chart-line': {
      // Tek günlük aralıkta (Günlük/Dün/Özel-tek-gün) günlük rapor tek nokta
      // verir → günlük varyantı (saatlik rapor) varsa ona çevir.
      const cfg = widget.config;
      if (isSingleDay && cfg.gunlukReportCode) {
        const gSourceId = kaynak(cfg.gunlukReportCode);
        if (gSourceId) {
          const gWidget = {
            ...widget,
            reportCode: cfg.gunlukReportCode,
            config: {
              ...cfg,
              categoryField: cfg.gunlukCategoryField ?? cfg.categoryField,
              valueField: cfg.gunlukValueField ?? cfg.valueField,
              title: cfg.gunlukTitle ?? cfg.title,
            },
          };
          return <LineChartWidget widget={gWidget} sourceId={gSourceId} parameters={parameters} />;
        }
      }
      return <LineChartWidget widget={widget} sourceId={sourceId} parameters={parameters} />;
    }
    case 'chart-bar':
      return <BarChartWidget widget={widget} sourceId={sourceId} parameters={parameters} />;
    case 'chart-pie':
      return <PieChartWidget widget={widget} sourceId={sourceId} parameters={parameters} />;
    case 'chart-donut':
      return <DonutChartWidget widget={widget} sourceId={sourceId} parameters={parameters} />;
    case 'chart-hbar':
      return <HBarWidget widget={widget} sourceId={sourceId} parameters={parameters} />;
    case 'table-summary':
      return <TableSummaryWidget widget={widget} sourceId={sourceId} parameters={parameters} />;
    case 'field-breakdown':
      return <FieldBreakdownWidget widget={widget} sourceId={sourceId} parameters={parameters} />;
    case 'open-tickets':
      return <OpenTicketsWidget widget={widget} sourceId={sourceId} parameters={parameters} />;
    case 'kabin-plan':
      // Salon "şu an"ı gösterir: dönem sekmesinden bağımsız, tarih parametresi almaz.
      return <KabinPlanWidget widget={widget} sourceId={sourceId} detaySourceId={kaynak(widget.config.detayReportCode)} />;
    case 'chart-area':
      return <AreaChartWidget widget={widget} sourceId={sourceId} parameters={parameters} />;
  }
}

function defaultSpan(widget: DashboardWidget): GridSpan {
  if (widget.gridSpan) return widget.gridSpan;
  // Firma widget'ları genişliği config'te taşıyabilir (API gridSpan alanı göndermez).
  const cfgSpan = (widget.config as { gridSpan?: unknown } | null)?.gridSpan;
  if (cfgSpan === 1 || cfgSpan === 2 || cfgSpan === 3 || cfgSpan === 4) return cfgSpan;
  if (widget.type === 'chart-area') return 4;
  if (widget.type === 'stat' || widget.type === 'kpi' || widget.type === 'stat-sparkline') return 1;
  if (widget.type === 'table-summary') return 3;
  if (widget.type === 'branch-control' || widget.type === 'open-tickets' || widget.type === 'kabin-plan') return 4;
  // chart-* and field-breakdown both default to half-width (col-6)
  return 2;
}

function widgetTitle(widget: DashboardWidget): string | null {
  if (widget.type === 'stat') return widget.config.label;
  // stat-sparkline + chart-* widgets all carry a `title` on their config.
  const cfg = widget.config as { title?: string } | null;
  return cfg?.title ?? null;
}

export function DynamicDashboard({ widgets, reports, widgetSources }: DynamicDashboardProps) {
  const normalized = widgets.map(normalizeWidget);
  const kaynak: KaynakBul = (code) =>
    code ? reports.find((r) => r.code === code)?.sourceId ?? widgetSources?.[code] : undefined;
  const today = dayjs();
  const [period, setPeriod] = useState<DashboardPeriod>('gunluk');
  const [customStart, setCustomStart] = useState(() => today.subtract(7, 'day').format('YYYY-MM-DD'));
  const [customEnd, setCustomEnd] = useState(() => today.format('YYYY-MM-DD'));

  const dateParams = (() => {
    const end = today.format('YYYY-MM-DD');
    let start = end;
    if (period === 'dun') {
      const yesterday = today.subtract(1, 'day').format('YYYY-MM-DD');
      return { '@BASTARIH': yesterday, '@BITTARIH': yesterday };
    }
    if (period === 'ozel') {
      // Ters aralık girilirse uçları sırala — sorgu hiç hata görmesin.
      const [s, e] = customStart <= customEnd ? [customStart, customEnd] : [customEnd, customStart];
      return { '@BASTARIH': s, '@BITTARIH': e };
    }
    if (period === 'aylik') start = today.startOf('month').format('YYYY-MM-DD');
    else if (period === 'yillik') start = '2026-01-01';
    return { '@BASTARIH': start, '@BITTARIH': end };
  })();

  const isSingleDay = dateParams['@BASTARIH'] === dateParams['@BITTARIH'];

  const periodLabel =
    period === 'gunluk' ? 'Bugün'
    : period === 'dun' ? 'Dün'
    : period === 'aylik' ? 'Bu ay'
    : period === 'yillik' ? 'Bu yıl (01.01.2026→bugün)'
    : `${dayjs(dateParams['@BASTARIH']).format('DD.MM.YYYY')} – ${dayjs(dateParams['@BITTARIH']).format('DD.MM.YYYY')}`;

  if (normalized.length === 0) {
    return (
      <>
        <PageHeader
          title="Özet"
          subtitle={`${today.format('D MMMM YYYY')} · Genel bakış`}
        />
        <EmptyState
          icon={I.Inbox}
          title="Dashboard henüz yapılandırılmamış"
          body="Panelinizi özelleştirmek için yöneticinizle iletişime geçin."
        />
      </>
    );
  }

  return (
    <>
      <div className="flex items-start justify-between mb-1">
        <PageHeader
          title="Özet"
          subtitle={`${today.format('D MMMM YYYY')} · ${periodLabel}`}
        />
        <div className="pt-1 flex flex-col items-end gap-2">
          <PeriodTabs value={period} onChange={setPeriod} />
          {period === 'ozel' && (
            <div className="inline-flex items-center gap-2 rounded-lg border border-border bg-surface2 px-2 py-1">
              <input
                type="date"
                data-testid="custom-range-start"
                value={customStart}
                max={today.format('YYYY-MM-DD')}
                onChange={(e) => setCustomStart(e.target.value)}
                className="bg-transparent text-sm text-text outline-none"
              />
              <span className="text-muted text-sm">–</span>
              <input
                type="date"
                data-testid="custom-range-end"
                value={customEnd}
                max={today.format('YYYY-MM-DD')}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="bg-transparent text-sm text-text outline-none"
              />
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-5">
        {normalized.map((widget, idx) => {
          const span = colSpanMap[defaultSpan(widget)];
          const key = `${widget.type}-${widget.reportCode}-${idx}`;
          const rendered = renderWidget(widget, kaynak, dateParams, isSingleDay);

          // Only table-summary renders bare content → it needs SectionCard chrome.
          // Charts + field-breakdown render their own <Panel> (card + title) and
          // stat widgets render their own StatCard chrome, so they pass through
          // as-is. Wrapping a Panel-rendering widget in SectionCard double-cards it
          // (nested borders + duplicated title), so we only wrap the bare one.
          if (widget.type === 'table-summary') {
            const title = widgetTitle(widget);
            return (
              <div key={key} className={`col-span-1 ${span}`}>
                <SectionCard title={title ?? undefined} bodyClass="p-4">
                  {rendered}
                </SectionCard>
              </div>
            );
          }

          return (
            <div key={key} className={`col-span-1 sm:col-span-1 ${span}`}>
              {rendered}
            </div>
          );
        })}
      </div>
    </>
  );
}
